import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import MaterialIcon from '@/components/ui/MaterialIcon';
import notificationService from '@/services/notificationService';
import { useAuthStore } from '@/store/authStore';
import { PRIMARY_NAV, SECONDARY_NAV, isActivePath, NavItem } from '@/config/navigation';

interface AppShellProps {
  children: React.ReactNode;
  /** Title used in the mobile header and the desktop top bar. */
  title?: string;
  /** When true the back button is rendered on mobile. */
  showBack?: boolean;
  backLabel?: string;
  onBack?: () => void;
  /** Slot displayed in the trailing area of the top bar (mobile + desktop). */
  rightElement?: React.ReactNode;
  /** Render the content at full width on desktop (for 2-column layouts). */
  wide?: boolean;
}

const SidebarLink: React.FC<{ item: NavItem; pathname: string; badge?: number }> = ({ item, pathname, badge }) => {
  const active = isActivePath(pathname, item.path);
  return (
    <li>
      <NavLink
        to={item.path}
        aria-current={active ? 'page' : undefined}
        className={`relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
          active
            ? 'bg-primary/10 text-primary dark:bg-primary/20'
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
        }`}
      >
        <MaterialIcon icon={item.icon} style={{ fontSize: 22 }} />
        <span className="truncate">{item.label}</span>
        {badge !== undefined && badge > 0 && (
          <span className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-white">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </NavLink>
    </li>
  );
};

const Sidebar: React.FC<{ unreadCount: number }> = ({ unreadCount }) => {
  const { pathname } = useLocation();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 md:left-0 bg-surface-light dark:bg-surface-dark border-r border-gray-200 dark:border-white/5">
      <div className="flex h-16 items-center gap-2 px-6 border-b border-gray-100 dark:border-white/5">
        <img src="/logo-leeris.png" alt="Leeris" className="h-9 w-auto rounded-lg bg-white px-2 py-1" />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Navigation principale">
        <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          Mon assurance
        </p>
        <ul className="flex flex-col gap-0.5">
          {PRIMARY_NAV.map((item) => (
            <SidebarLink key={item.path} item={item} pathname={pathname} />
          ))}
        </ul>

        <p className="px-3 pt-6 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          Plus
        </p>
        <ul className="flex flex-col gap-0.5">
          {SECONDARY_NAV.map((item) => (
            <SidebarLink
              key={item.path}
              item={item}
              pathname={pathname}
              badge={item.path === '/notifications' ? unreadCount : undefined}
            />
          ))}
        </ul>
      </nav>

      <div className="border-t border-gray-100 dark:border-white/5 p-3">
        <div className="flex items-center gap-3 rounded-xl p-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold">
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="truncate text-xs text-gray-500 dark:text-gray-400">
              {user?.memberNumber}
            </p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            aria-label="Se déconnecter"
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <MaterialIcon icon="logout" style={{ fontSize: 20 }} />
          </button>
        </div>
      </div>
    </aside>
  );
};

const BottomNav: React.FC<{ unreadCount: number }> = ({ unreadCount }) => {
  const { pathname } = useLocation();
  return (
    <nav
      aria-label="Navigation principale"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-surface-light dark:bg-surface-dark border-t border-gray-100 dark:border-white/5 pb-safe"
    >
      <ul className="mx-auto grid max-w-[480px] grid-cols-5">
        {PRIMARY_NAV.map((item) => {
          const active = isActivePath(pathname, item.path);
          const showBadge = item.path === '/profile' && unreadCount > 0;
          return (
            <li key={item.path}>
              <NavLink
                to={item.path}
                aria-current={active ? 'page' : undefined}
                className={`flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${
                  active ? 'text-primary' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                <span className="relative">
                  <MaterialIcon icon={item.icon} style={{ fontSize: 24 }} />
                  {showBadge && (
                    <span aria-hidden="true" className="absolute -top-0.5 -right-1 h-2 w-2 rounded-full bg-error" />
                  )}
                </span>
                <span>{item.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

const MobileHeader: React.FC<{
  title?: string;
  showBack: boolean;
  backLabel: string;
  onBack?: () => void;
  rightElement?: React.ReactNode;
}> = ({ title, showBack, backLabel, onBack, rightElement }) => {
  const navigate = useNavigate();
  const handleBack = () => (onBack ? onBack() : navigate(-1));

  if (!title && !showBack && !rightElement) return null;

  return (
    <header className="md:hidden sticky top-0 z-30 flex items-center justify-between gap-2 px-4 py-3 bg-surface-light/90 dark:bg-background-dark/90 backdrop-blur-xl border-b border-gray-100 dark:border-white/5">
      <div className="flex-1 flex items-center">
        {showBack && (
          <button
            type="button"
            onClick={handleBack}
            aria-label={backLabel}
            className="flex items-center gap-1.5 rounded-xl px-2 py-1.5 text-gray-600 dark:text-gray-400 hover:text-primary"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-white/5">
              <MaterialIcon icon="arrow_back" style={{ fontSize: 20 }} />
            </span>
            <span className="text-xs font-bold uppercase tracking-widest">{backLabel}</span>
          </button>
        )}
      </div>
      <h1 className="flex-[2] text-center truncate text-base font-bold text-gray-900 dark:text-white">
        {title}
      </h1>
      <div className="flex-1 flex justify-end">{rightElement}</div>
    </header>
  );
};

const DesktopTopbar: React.FC<{
  title?: string;
  rightElement?: React.ReactNode;
}> = ({ title, rightElement }) => {
  if (!title && !rightElement) return null;
  return (
    <header className="hidden md:flex sticky top-0 z-30 h-16 items-center justify-between gap-4 px-8 bg-surface-light/90 dark:bg-background-dark/90 backdrop-blur-xl border-b border-gray-100 dark:border-white/5">
      {title && (
        <h1 className="text-xl font-display font-bold text-gray-900 dark:text-white">{title}</h1>
      )}
      <div className="ml-auto flex items-center gap-3">{rightElement}</div>
    </header>
  );
};

const AppShell: React.FC<AppShellProps> = ({
  children,
  title,
  showBack = false,
  backLabel = 'Retour',
  onBack,
  rightElement,
  wide = false,
}) => {
  const { data: unreadData } = useQuery({
    queryKey: ['unreadCount'],
    queryFn: () => notificationService.getUnreadCount(),
    staleTime: 60_000,
  });
  const unreadCount = unreadData ?? 0;

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark">
      <Sidebar unreadCount={unreadCount} />

      <div className="md:pl-64 flex min-h-screen flex-col">
        <MobileHeader
          title={title}
          showBack={showBack}
          backLabel={backLabel}
          onBack={onBack}
          rightElement={rightElement}
        />
        <DesktopTopbar title={title} rightElement={rightElement} />

        <main
          className={`flex-1 w-full mx-auto pb-24 md:pb-8 max-w-[480px] md:px-8 ${
            wide ? 'md:max-w-none' : 'md:max-w-5xl'
          }`}
        >
          {children}
        </main>
      </div>

      <BottomNav unreadCount={unreadCount} />
    </div>
  );
};

export default AppShell;
